// Audiences ("chips") — who may receive a piece of knowledge. One list per community, seeded by
// the industry pack, edited in Settings → Audiences. Cached per tenant, shared by every picker.
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

export const OPEN = "anyone";
export const ALL_PEOPLE = "residents";
export const STAFF = "staff";
export const BOARD = "board";

const cache = {};      // tenantId -> payload
const waiters = {};    // tenantId -> Set(setState)

export function invalidateAudiences(tenantId) {
  delete cache[tenantId];
  (waiters[tenantId] || new Set()).forEach((fn) => fn(null));
}

async function fetchAudiences(tenantId) {
  const r = await api.get(`/tenants/${tenantId}/audiences`);
  cache[tenantId] = r.data;
  (waiters[tenantId] || new Set()).forEach((fn) => fn(r.data));
  return r.data;
}

export function useAudiences(tenantId) {
  const [data, setData] = useState(tenantId ? cache[tenantId] || null : null);
  useEffect(() => {
    if (!tenantId) return undefined;
    waiters[tenantId] = waiters[tenantId] || new Set();
    waiters[tenantId].add(setData);
    if (cache[tenantId]) setData(cache[tenantId]);
    else fetchAudiences(tenantId).catch(() => setData({ audiences: [], default_key: OPEN, error: true }));
    return () => { waiters[tenantId]?.delete(setData); };
  }, [tenantId]);
  useEffect(() => { if (tenantId && data === null && cache[tenantId] === undefined) fetchAudiences(tenantId).catch(() => {}); }, [tenantId, data]);
  const reload = useCallback(() => (tenantId ? fetchAudiences(tenantId) : Promise.resolve(null)), [tenantId]);
  const list = data?.audiences || [];
  const byKey = Object.fromEntries(list.map((a) => [a.key, a]));
  const labels = { [OPEN]: "Anyone", ...Object.fromEntries(list.map((a) => [a.key, a.label])) };
  return {
    list, byKey, labels, reload,
    loading: data === null,
    defaultKey: data?.default_key || OPEN,
    openItems: data?.open_items ?? 0,
    defaultPersonChips: data?.default_person_chips || [],
    people: list.filter((a) => a.group !== "team"),
    team: list.filter((a) => a.group === "team"),
    manual: list.filter((a) => a.kind !== "auto"),
    max: data?.max || 25,
  };
}

// Chips on an item, tolerant of legacy single-word `audience`.
export function chipsOf(item) {
  const a = item?.audiences;
  if (Array.isArray(a) && a.length) return a;
  const w = (item?.audience || OPEN).toLowerCase();
  return [OPEN, ALL_PEOPLE, BOARD].includes(w) ? [w] : [OPEN];
}

export function describeChips(chips, labels) {
  const c = chips || [];
  if (!c.length || c.includes(OPEN)) return labels?.[OPEN] || "Anyone";
  return c.map((k) => labels?.[k] || k).join(" · ");
}

export const isOpen = (chips) => !chips || !chips.length || chips.includes(OPEN);
